/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/

import React, { useState, useMemo } from 'react';
import { Wand2, Check } from 'lucide-react';
import { platformsDefinitions, categoriesToAspectRatios } from './platformDefinitions';
import { SpinnerIcon } from './Spinner';

interface CropPanelProps {
  onApplyCrop: () => void;
  onApplyAICrop: (aspects: string[]) => void;
  onSetAspect: (aspect: number | undefined) => void;
  isLoading: boolean;
  isSubmitting: boolean;
  isCropping: boolean;
  hasImage: boolean;
}

const getAspectRatioStyle = (ratio: string): React.CSSProperties => {
  if (ratio === 'free') return { aspectRatio: '1/1', borderStyle: 'dashed' };
  const [w, h] = ratio.split(':').map(Number);
  return { aspectRatio: `${w}/${h}` };
};

const parseRatioValue = (ratio: string): number | undefined => {
    if (ratio === 'free') return undefined;
    const [w, h] = ratio.split(':').map(Number);
    return w / h;
};

const CropPanel: React.FC<CropPanelProps> = ({ onApplyCrop, onApplyAICrop, onSetAspect, isLoading, isSubmitting, isCropping, hasImage }) => {
  const [activeAspect, setActiveAspect] = useState<string>('free');
  const [selectedAspects, setSelectedAspects] = useState<Set<string>>(new Set());

  const { uniqueRatios, uniqueCategories } = useMemo(() => {
    const ratios = new Map<string, { name: string; label: string; value: number | undefined; platforms: Set<string> }>();
    const cats = new Map<string, { label: string; logos: string[] }>();

    // Add 'free' manually
    ratios.set('free', { name: 'free', label: 'Free', value: undefined, platforms: new Set(['General']) });

    Object.entries(platformsDefinitions).forEach(([platformKey, def]) => {
      def.categories.forEach((cat) => {
        if (!cats.has(cat)) {
          cats.set(cat, { label: cat, logos: [] });
        }
        if (def.logo && !cats.get(cat)?.logos.includes(def.logo)) {
          cats.get(cat)?.logos.push(def.logo);
        }

        const catAspects = categoriesToAspectRatios[cat] || [];
        
        catAspects.forEach((aspectOption) => {
          const aspectName = aspectOption.value;
          const aspectLabel = aspectOption.label;
          const aspectValue = parseRatioValue(aspectName);

          if (!ratios.has(aspectName)) {
            ratios.set(aspectName, { name: aspectName, label: aspectLabel, value: aspectValue, platforms: new Set() });
          }
          ratios.get(aspectName)?.platforms.add(platformKey);
        });
      });
    });

    const sortedRatios = Array.from(ratios.values()).sort((a, b) => {
      if (a.name === 'free') return -1;
      if (b.name === 'free') return 1;
      return a.name.localeCompare(b.name);
    });

    return {
      uniqueRatios: sortedRatios,
      uniqueCategories: Array.from(cats.values()),
    };
  }, []);
  
  const handleRatioClick = (ratioName: string, ratioValue: number | undefined, event: React.MouseEvent) => {
    // Check if Shift or Cmd/Ctrl is pressed for multi-select (AI crop)
    const isMultiSelect = event.shiftKey || event.metaKey || event.ctrlKey;
    
    if (ratioName === 'free') {
      setActiveAspect('free');
      onSetAspect(undefined);
      if (!isMultiSelect) {
        setSelectedAspects(new Set());
      }
      // Don't add 'free' to selected aspects for AI crop
      return;
    }

    if (isMultiSelect) {
      // Multi-select mode: toggle selection for AI crop
      const newSelected = new Set(selectedAspects);
      if (newSelected.has(ratioName)) {
        newSelected.delete(ratioName);
      } else {
        newSelected.add(ratioName);
      }
      setSelectedAspects(newSelected);
      
      // Also set as active for manual crop
      setActiveAspect(ratioName);
      onSetAspect(ratioValue);
    } else {
      // Single-select mode: set as active for manual crop only
      setActiveAspect(ratioName);
      onSetAspect(ratioValue);
      
      setSelectedAspects(new Set([ratioName]))
    }
  };

  const toggleCategory = (category: string) => {
    const ratios = categoriesToAspectRatios[category];
    if (!ratios) return;

    const ratioValues = ratios.map(r => r.value).filter(r => r !== 'free');
    if (ratioValues.length === 0) return;

    const allSelected = ratioValues.every((r) => selectedAspects.has(r));
    const newSelected = new Set(selectedAspects);

    if (allSelected) {
      ratioValues.forEach((r) => newSelected.delete(r));
    } else {
      ratioValues.forEach((r) => newSelected.add(r));
    }
    setSelectedAspects(newSelected);
  };

  return (
    <div className="w-full bg-gray-800/50 border border-gray-700 rounded-lg p-4 flex flex-col gap-6 animate-fade-in backdrop-blur-sm">
      <div className="w-full bg-gradient-to-r from-blue-500/10 to-purple-500/10 backdrop-blur-xl border border-blue-500/20 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Wand2 className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-gray-200 mb-1">
              How to crop
              {selectedAspects.size > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs font-bold bg-blue-500 text-white rounded-full">
                  {selectedAspects.size}
                </span>
              )}
            </p>
            <p className="text-xs text-gray-400 leading-relaxed">
              Click to select for manual crop. Hold ⌘/Ctrl and click to select multiple for AI crop.
            </p>
          </div>
        </div>
      </div>

      {/* Platform Presets */}
      <div className="space-y-2">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Quick Select (AI Crop)</p>
        <div className="flex flex-wrap gap-2">
          {uniqueCategories.map((cat) => {
             // Check if all ratios in this category are selected
             const ratios = categoriesToAspectRatios[cat.label];
             const ratioValues = ratios?.map(r => r.value).filter(r => r !== 'free') || [];
             const isFullySelected = ratioValues.length > 0 && ratioValues.every(r => selectedAspects.has(r));
             const isPartiallySelected = !isFullySelected && ratioValues.some(r => selectedAspects.has(r));
             
             return (
               <button
                 key={cat.label}
                 onClick={() => toggleCategory(cat.label)}
                 className={`
                   flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all border
                   ${isFullySelected 
                     ? 'bg-blue-500/20 border-blue-500/50 text-blue-200' 
                     : isPartiallySelected
                        ? 'bg-gray-700/50 border-gray-600 text-gray-200'
                        : 'bg-gray-800/50 border-gray-700 text-gray-400 hover:bg-gray-700/50'
                   }
                 `}
               >
                 <div className="flex -space-x-2 mr-1">
                   {cat.logos.map((logo, i) => (
                     <img key={i} src={logo} alt="" className="w-4 h-4 object-contain rounded-full bg-white/10 ring-1 ring-gray-700" style={{ zIndex: cat.logos.length - i }} />
                   ))}
                 </div>
                 {cat.label}
                 {isFullySelected && <Check className="w-3 h-3 ml-1" />}
               </button>
             );
          })}
        </div>
      </div>

      {/* Aspect Ratios Grid */}
      <div className="space-y-2">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Available Formats</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {uniqueRatios.map((ratio) => {
            const isSelectedForAI = selectedAspects.has(ratio.name);
            const isActive = activeAspect === ratio.name;
            
            return (
              <button
                key={ratio.name}
                onClick={(e) => handleRatioClick(ratio.name, ratio.value, e)}
                disabled={isLoading}
                className={`
                  relative group flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all duration-200
                  ${isActive
                    ? 'bg-green-500/10 border-green-500 shadow-lg shadow-green-500/10' 
                    : isSelectedForAI
                        ? 'bg-blue-500/10 border-blue-500/50 shadow-md shadow-blue-500/5'
                        : 'bg-gray-800/40 border-gray-700 hover:border-gray-600 hover:bg-gray-700/40'
                  }
                `}
              >
                {/* Visual Aspect Ratio Box */}
                <div 
                  className={`w-8 mb-2 rounded-sm border-2 transition-colors ${
                    isActive 
                      ? 'border-green-400 bg-green-400/20' 
                      : isSelectedForAI 
                        ? 'border-blue-400 bg-blue-400/20' 
                        : 'border-gray-500 bg-gray-500/10'
                  }`}
                  style={getAspectRatioStyle(ratio.name)}
                />
                
                <span className={`text-sm font-bold ${
                  isActive 
                    ? 'text-green-200' 
                    : isSelectedForAI 
                      ? 'text-blue-200' 
                      : 'text-gray-300'
                }`}>
                  {ratio.label}
                </span>
                
                {/* Platform dots */}
                <div className="flex gap-1 mt-2">
                   {Array.from(ratio.platforms).slice(0, 4).map(p => (
                     <div 
                       key={p} 
                       className={`w-1 h-1 rounded-full ${
                         isActive 
                           ? 'bg-green-400/50' 
                           : isSelectedForAI 
                             ? 'bg-blue-400/50' 
                             : 'bg-gray-600'
                       }`} 
                       title={p}
                     />
                   ))}
                </div>
                
                {/* Active indicator (green checkmark for manual crop) */}
                {isActive && (
                  <div className="absolute top-2 right-2">
                    <div className="bg-green-500 rounded-full p-0.5">
                      <Check className="w-2 h-2 text-white" />
                    </div>
                  </div>
                )}
                
                {/* Selected for AI indicator (blue checkmark) */}
                {isSelectedForAI && !isActive && (
                  <div className="absolute top-2 right-2">
                    <div className="bg-blue-500 rounded-full p-0.5">
                      <Check className="w-2 h-2 text-white" />
                    </div>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <button
        onClick={onApplyCrop}
        disabled={isLoading || isSubmitting || !isCropping || !hasImage}
        className="w-full mt-2 bg-gradient-to-br from-green-600 to-green-500 text-white font-bold py-4 px-6 rounded-lg transition-all duration-300 ease-in-out shadow-lg shadow-green-500/20 hover:shadow-xl hover:shadow-green-500/40 hover:-translate-y-px active:scale-95 active:shadow-inner text-base disabled:from-green-800 disabled:to-green-700 disabled:shadow-none disabled:cursor-not-allowed disabled:transform-none"
      >
        Apply Manual Crop ({activeAspect === 'free' ? 'Free' : uniqueRatios.find(r => r.name === activeAspect)?.label || activeAspect})
      </button>

      <button
        onClick={() => onApplyAICrop(Array.from(selectedAspects))}
        disabled={isLoading || isSubmitting || selectedAspects.size === 0 || !hasImage}
        className="w-full mt-2 bg-gradient-to-br from-yellow-600 to-yellow-500 text-white font-bold py-4 px-6 rounded-lg transition-all duration-300 ease-in-out shadow-lg shadow-yellow-500/20 hover:shadow-xl hover:shadow-yellow-500/40 hover:-translate-y-px active:scale-95 active:shadow-inner text-base disabled:from-yellow-800 disabled:to-yellow-700 disabled:shadow-none disabled:cursor-not-allowed disabled:transform-none"
      >
        {isSubmitting ? (
          <span className="flex items-center justify-center gap-2">
            <SpinnerIcon />
            Submitting...
          </span>
        ) : (
          selectedAspects.size > 1 ? `Apply AI Crop (${selectedAspects.size} formats)` : selectedAspects.size === 1 ? 'Apply AI Crop (1 format)' : 'Apply AI Crop'
        )}
      </button>
    </div>
  );
};

export default CropPanel;
